/**
 * Custom hook for inline editing functionality in workspace header
 * 
 * This hook manages the state and handlers for inline editing of
 * workspace title and description fields. It provides a consistent
 * pattern for edit/save/cancel operations with proper error handling.
 * 
 * @module useWorkspaceHeaderInlineEdit
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { patchUpdatesBasicWorkspaceDetails } from '@/apis/core/workspace/patch/patchUpdatesBasicWorkspaceDetails';
import { useWorkspaceErrorHandler } from '@/hooks/useWorkspaceErrorHandler';
import { ApiError } from '@/utils/errors/apiErrorWrapper';
import {
  extractErrorResponse,
  normalizeError,
} from '../utils/workspaceHeaderUtils';

/**
 * Parameters for the useWorkspaceHeaderInlineEdit hook
 */
interface UseWorkspaceHeaderInlineEditParams {
  /** Workspace ID */
  workspaceId: string;
  /** Current workspace title */
  title: string;
  /** Current workspace description */
  description: string;
  /** Function to get access token for API calls */
  getAccessToken: () => Promise<string | null>;
  /** Function to refresh workspace data after updates */
  refreshWorkspaces: () => Promise<void>;
}

/**
 * Return type for the useWorkspaceHeaderInlineEdit hook
 */
interface UseWorkspaceHeaderInlineEditReturn {
  // Title editing
  isEditingTitle: boolean;
  editedTitle: string;
  setEditedTitle: (title: string) => void;
  titleInputRef: React.RefObject<HTMLInputElement>;
  handleStartEditTitle: () => void;
  handleCancelEditTitle: () => void;
  handleSaveTitle: () => Promise<void>;
  
  // Description editing
  isEditingDescription: boolean;
  editedDescription: string;
  setEditedDescription: (description: string) => void;
  descriptionInputRef: React.RefObject<HTMLTextAreaElement>;
  handleStartEditDescription: () => void;
  handleCancelEditDescription: () => void;
  handleSaveDescription: () => Promise<void>;
  
  // Shared state
  isSaving: boolean;
}

/**
 * Custom hook for managing inline editing of workspace title and description
 * 
 * This hook provides:
 * - Separate state management for title and description editing
 * - Auto-focus and select on edit start
 * - Validation (title cannot be empty)
 * - API calls with proper error handling
 * - Success/error toast notifications
 * 
 * @param params - Hook parameters
 * @returns Inline editing state and handlers
 * 
 * @example
 * ```tsx
 * const {
 *   isEditingTitle,
 *   editedTitle,
 *   handleStartEditTitle,
 *   handleSaveTitle,
 *   handleCancelEditTitle,
 * } = useWorkspaceHeaderInlineEdit({
 *   workspaceId,
 *   title: workspaceData.title,
 *   description: workspaceData.description,
 *   getAccessToken,
 *   refreshWorkspaces,
 * });
 * ```
 */
export const useWorkspaceHeaderInlineEdit = ({
  workspaceId,
  title,
  description,
  getAccessToken,
  refreshWorkspaces,
}: UseWorkspaceHeaderInlineEditParams): UseWorkspaceHeaderInlineEditReturn => {
  // ============= Error Handling =============
  const { handleError, handleSuccess, handleWarning } = useWorkspaceErrorHandler(workspaceId);

  // ============= State =============
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedTitle, setEditedTitle] = useState(title);
  const [editedDescription, setEditedDescription] = useState(description);
  const [isSaving, setIsSaving] = useState(false);
  
  const titleInputRef = useRef<HTMLInputElement>(null);
  const descriptionInputRef = useRef<HTMLTextAreaElement>(null);

  // ============= Effects =============

  /**
   * Sync edited values when workspace data changes
   * This ensures the edited values stay in sync with the latest workspace data
   */
  useEffect(() => {
    setEditedTitle(title);
    setEditedDescription(description);
  }, [title, description]);

  // ============= Title Handlers =============

  /**
   * Starts editing the title
   * 
   * Sets editing state, resets edited value to current title,
   * and focuses/selects the input field.
   */
  const handleStartEditTitle = useCallback(() => {
    setIsEditingTitle(true);
    setEditedTitle(title);
    setTimeout(() => {
      titleInputRef.current?.focus();
      titleInputRef.current?.select();
    }, 0);
  }, [title]);

  /**
   * Cancels editing the title
   * 
   * Resets edited value to current title and exits edit mode.
   */
  const handleCancelEditTitle = useCallback(() => {
    setIsEditingTitle(false);
    setEditedTitle(title);
  }, [title]);

  /**
   * Saves the edited title
   * 
   * Validates the title (cannot be empty), makes API call,
   * and handles success/error states with appropriate notifications.
   */
  const handleSaveTitle = useCallback(async () => {
    const trimmedTitle = editedTitle.trim();
    
    // Validation: Title cannot be empty
    if (!trimmedTitle) {
      handleWarning('Title cannot be empty');
      return;
    }

    // No changes - just exit edit mode
    if (trimmedTitle === title) {
      setIsEditingTitle(false);
      return;
    }

    try {
      setIsSaving(true);
      await patchUpdatesBasicWorkspaceDetails({
        workspaceId,
        getAccessToken,
        body: {
          title: trimmedTitle,
        },
      });
      setIsEditingTitle(false);
      await refreshWorkspaces();
      handleSuccess('Title updated successfully');
    } catch (err) {
      const error = normalizeError(err, 'Failed to update title');
      const response = extractErrorResponse(err);
      
      console.error('Error updating workspace title:', error);
      handleError(
        error instanceof ApiError ? error : error,
        'Failed to update title',
        response
      );
    } finally {
      setIsSaving(false);
    }
  }, [editedTitle, workspaceId, title, getAccessToken, refreshWorkspaces, handleError, handleSuccess, handleWarning]);

  // ============= Description Handlers =============

  /**
   * Starts editing the description
   * 
   * Sets editing state, resets edited value to current description,
   * and focuses/selects the textarea field.
   */
  const handleStartEditDescription = useCallback(() => {
    setIsEditingDescription(true);
    setEditedDescription(description);
    setTimeout(() => {
      descriptionInputRef.current?.focus();
      descriptionInputRef.current?.select();
    }, 0);
  }, [description]);

  /**
   * Cancels editing the description
   * 
   * Resets edited value to current description and exits edit mode.
   */
  const handleCancelEditDescription = useCallback(() => {
    setIsEditingDescription(false);
    setEditedDescription(description);
  }, [description]);

  /**
   * Saves the edited description
   * 
   * Makes API call if description changed and handles
   * success/error states with appropriate notifications.
   */
  const handleSaveDescription = useCallback(async () => {
    const trimmedDescription = editedDescription.trim();
    
    // No changes - just exit edit mode
    if (trimmedDescription === description) {
      setIsEditingDescription(false);
      return;
    }

    try {
      setIsSaving(true);
      await patchUpdatesBasicWorkspaceDetails({
        workspaceId,
        getAccessToken,
        body: {
          description: trimmedDescription,
        },
      });
      setIsEditingDescription(false);
      await refreshWorkspaces();
      handleSuccess('Description updated successfully');
    } catch (err) {
      const error = normalizeError(err, 'Failed to update description');
      const response = extractErrorResponse(err);
      
      console.error('Error updating workspace description:', error);
      handleError(
        error instanceof ApiError ? error : error,
        'Failed to update description',
        response
      );
    } finally {
      setIsSaving(false);
    }
  }, [editedDescription, workspaceId, description, getAccessToken, refreshWorkspaces, handleError, handleSuccess]);

  return {
    // Title editing
    isEditingTitle,
    editedTitle,
    setEditedTitle,
    titleInputRef,
    handleStartEditTitle,
    handleCancelEditTitle,
    handleSaveTitle,
    
    // Description editing
    isEditingDescription,
    editedDescription,
    setEditedDescription,
    descriptionInputRef,
    handleStartEditDescription,
    handleCancelEditDescription,
    handleSaveDescription,
    
    // Shared state
    isSaving,
  };
};
